import { ListPromptVersion } from "@/types";
import { computed, makeAutoObservable } from "mobx";
import { useLocalObservable } from "mobx-react-lite";
import { createContext, useContext } from "react";
import { DateRange } from "react-day-picker";
import { ReleaseLabel } from "./ReleaseLabelSelector";
import { NameWithId } from "./types";

interface FilterState {
  evaluation?: NameWithId;
  dataset?: NameWithId;
  releaseLabel?: ReleaseLabel;
  promptVersion?: ListPromptVersion;
  dateRange?: DateRange;
}

class HistoryFilterStore {
  private evaluation?: NameWithId;
  private dataset?: NameWithId;
  private releaseLabel?: ReleaseLabel;
  private promptVersion?: ListPromptVersion;
  private dateRange?: DateRange;
  private previousFilter?: FilterState;

  constructor() {
    makeAutoObservable(this, {
      filterState: computed,
    });
  }

  get filterState() {
    return {
      evaluation: this.evaluation,
      dataset: this.dataset,
      releaseLabel: this.releaseLabel,
      promptVersion: this.promptVersion,
      dateRange: this.dateRange,
      previousFilter: this.previousFilter,
    };
  }

  setDateRange(range: DateRange | undefined) {
    this.dateRange = range;
  }

  get selectedDateRange() {
    return this.dateRange;
  }

  setReleaseLabel(label: ReleaseLabel) {
    this.releaseLabel = label;
  }

  removeReleaseLabel() {
    this.releaseLabel = undefined;
  }

  get selectedLabel() {
    return this.releaseLabel;
  }

  clearReleaseLabel() {
    this.releaseLabel = undefined;
  }

  get selectedVersion() {
    return this?.promptVersion;
  }

  get selectedVersionId() {
    return this?.promptVersion?.id;
  }

  removeVersion() {
    this.promptVersion = undefined;
  }

  setVersion(version: ListPromptVersion) {
    this.promptVersion = version;
  }

  removeEvaluation() {
    this.evaluation = undefined;
  }

  clearAllFilters() {
    this.evaluation = undefined;
    this.dataset = undefined;
    this.clearReleaseLabel();
    this.dateRange = undefined;
    this.promptVersion = undefined;
  }

  setEvaluation(data: NameWithId | undefined) {
    this.evaluation = data;
    this.dataset = undefined;
  }

  setDataset(data: NameWithId | undefined) {
    this.dataset = data;
  }

  get evaluationName() {
    return this.evaluation?.name;
  }

  get selectedDataset() {
    return this.dataset;
  }

  removeDataset() {
    this.dataset = undefined;
  }

  get selectedEvaluation() {
    return this.evaluation;
  }

  get selectedLabelId() {
    return this.releaseLabel?.id;
  }

  get selectedEvaluationId() {
    return this.evaluation?.id;
  }

  get selectedDatasetId() {
    return this.dataset?.id;
  }

  saveCurrentFilterAsPrevious() {
    this.previousFilter = { ...this.filterState };
  }

  loadPreviousFilter() {
    if (!this.previousFilter) return;

    if (this.previousFilter.evaluation) {
      this.setEvaluation(this.previousFilter.evaluation);
    } else {
      this.removeEvaluation();
    }

    if (this.previousFilter.dataset) {
      this.setDataset(this.previousFilter.dataset);
    } else {
      this.removeDataset();
    }

    this.releaseLabel = this.previousFilter.releaseLabel;

    this.dateRange = this.previousFilter.dateRange;

    this.promptVersion = this.previousFilter.promptVersion;
  }

  get previousFilterState() {
    return this.previousFilter;
  }

  get count() {
    return (
      (this.evaluation ? 1 : 0) +
      (this.dataset ? 1 : 0) +
      (this.releaseLabel ? 1 : 0) +
      (this.promptVersion ? 1 : 0) +
      (this.dateRange ? 1 : 0)
    );
  }

  get filters() {
    return {
      evaluationId: this.evaluation?.id,
      datasetId: this.dataset?.id,
      releaseLabel: this.releaseLabel,
      dateRange: this.dateRange,
      promptVersion: this.promptVersion,
    };
  }
}

export const HistoryFilterStoreContext =
  createContext<HistoryFilterStore | null>(null);

export const HistoryStoreProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const store = useLocalObservable(() => new HistoryFilterStore());
  return (
    <HistoryFilterStoreContext.Provider value={store}>
      {children}
    </HistoryFilterStoreContext.Provider>
  );
};

export const useHistoryFilters = () => {
  const store = useContext(HistoryFilterStoreContext);
  if (!store) {
    throw new Error(
      "useHistoryFilters must be used within a HistoryStoreProvider",
    );
  }
  return store;
};
export const withHistoryState = <P extends object>(
  Component: React.ComponentType<P>,
) => {
  return (props: P) => {
    return (
      <HistoryStoreProvider>
        <Component {...props} />
      </HistoryStoreProvider>
    );
  };
};
